import type { EvidenceFlags, ReportOutsideFactor } from 'definitions';
import { EXCLUDING_OUTSIDE_FACTORS } from 'definitions';

/** One line under "What it taught". `experimentId` is set when the line points at another test. */
export type EvidenceNote = { key: string; text: string; experimentId?: number };

const EXCLUDING = EXCLUDING_OUTSIDE_FACTORS as readonly ReportOutsideFactor[];

/**
 * What the team ticked, said as the team's report and not as a fact. The two factors that
 * take a test out of patterns say so, because that is a consequence the reader would
 * otherwise have to guess at.
 */
const factorNote = (factor: ReportOutsideFactor): string => {
  const left = EXCLUDING.includes(factor) ? ', so this result is left out of patterns' : '';
  switch (factor) {
    case 'promotion':
      return 'The team noted a promotion ran during this test.';
    case 'seasonality':
      return 'The team noted seasonality may have affected this test.';
    case 'traffic-change':
      return 'The team noted a change in traffic during this test.';
    case 'site-release':
      return `The team noted a site release during this test${left}.`;
    case 'tracking-issue':
      return `The team noted a tracking issue${left}.`;
    case 'other':
      return 'The team noted something else that may have affected this test.';
  }
};

/**
 * The evidence flags as short sentences, only those that are set. Computed flags first, then
 * the team's. No debrief adds no line: the absence of a note is not a claim that nothing
 * interfered, so nothing says so.
 *
 * A no-data test gets no notes at all: its own message, "No result to learn from", already
 * says the only thing worth saying, and "too few visitors" beside it would repeat it.
 */
export const evidenceNotes = (flags: EvidenceFlags): EvidenceNote[] => {
  if (flags.noData) return [];

  const notes: EvidenceNote[] = [];
  if (flags.srm === true) {
    notes.push({ key: 'srm', text: 'The traffic split was off, so this result is left out of patterns.' });
  }
  if (flags.underpowered) notes.push({ key: 'underpowered', text: 'Too few visitors to be sure either way.' });
  if (flags.shortRun === true) {
    notes.push({ key: 'short-run', text: 'Ran for under a week, so it never covered a full weekly cycle.' });
  }
  if (flags.interaction) {
    notes.push({
      key: 'interaction',
      text: `Overlapped #${flags.interaction.otherExperimentId} on the same page, which may have affected both.`,
      experimentId: flags.interaction.otherExperimentId,
    });
  }
  for (const factor of flags.outsideFactors) notes.push({ key: `factor-${factor}`, text: factorNote(factor) });
  return notes;
};
